// components/ui/iconSets.ts
// Lazy registry for react-icons sets. Each set is dynamically imported so the
// ~36 MB of react-icons never lands in the main bundle — a set's chunk is only
// fetched when an icon from it is actually rendered.
import type { IconType } from 'react-icons';

export interface IconSetMeta {
    name: string;
    prefix: string;
    color: string;
    category: string;
    package: string;
    load: () => Promise<unknown>;
}

type IconModule = Record<string, IconType>;

// Explicit import() literals — the bundler needs them statically analyzable to
// emit a chunk per set.
export const ICON_SETS: IconSetMeta[] = [
    { name: 'Font Awesome 6', prefix: 'Fa', color: '#528DD7', category: 'Popular', package: 'fa6', load: () => import('react-icons/fa6') },
    { name: 'Font Awesome', prefix: 'Fa', color: '#528DD7', category: 'Popular', package: 'fa', load: () => import('react-icons/fa') },
    { name: 'Material Design', prefix: 'Md', color: '#FF5722', category: 'Popular', package: 'md', load: () => import('react-icons/md') },
    { name: 'Heroicons 2', prefix: 'Hi', color: '#3B82F6', category: 'Popular', package: 'hi2', load: () => import('react-icons/hi2') },
    { name: 'Heroicons', prefix: 'Hi', color: '#3B82F6', category: 'Popular', package: 'hi', load: () => import('react-icons/hi') },
    { name: 'Ionicons 5', prefix: 'Io', color: '#3880FF', category: 'Popular', package: 'io5', load: () => import('react-icons/io5') },
    { name: 'Feather', prefix: 'Fi', color: '#4A5568', category: 'Popular', package: 'fi', load: () => import('react-icons/fi') },
    { name: 'Lucide', prefix: 'Lu', color: '#3B82F6', category: 'Popular', package: 'lu', load: () => import('react-icons/lu') },
    { name: 'Simple Icons', prefix: 'Si', color: '#000000', category: 'Brands', package: 'si', load: () => import('react-icons/si') },
    { name: 'Bootstrap', prefix: 'Bs', color: '#7952B3', category: 'UI Frameworks', package: 'bs', load: () => import('react-icons/bs') },
    { name: 'Ant Design', prefix: 'Ai', color: '#1890FF', category: 'UI Frameworks', package: 'ai', load: () => import('react-icons/ai') },
    { name: 'Remix Icon', prefix: 'Ri', color: '#25B864', category: 'UI Frameworks', package: 'ri', load: () => import('react-icons/ri') },
    { name: 'Radix UI', prefix: 'Rx', color: '#6B7280', category: 'UI Frameworks', package: 'rx', load: () => import('react-icons/rx') },
    { name: 'Tabler Icons', prefix: 'Tb', color: '#206BC4', category: 'UI Frameworks', package: 'tb', load: () => import('react-icons/tb') },
    { name: 'Phosphor', prefix: 'Pi', color: '#60A5FA', category: 'UI Frameworks', package: 'pi', load: () => import('react-icons/pi') },
    { name: 'Google Material', prefix: 'Fc', color: '#4285F4', category: 'Google', package: 'fc', load: () => import('react-icons/fc') },
    { name: 'Grommet', prefix: 'Gr', color: '#7D4CDB', category: 'UI Frameworks', package: 'gr', load: () => import('react-icons/gr') },
    { name: 'Devicon', prefix: 'Di', color: '#333333', category: 'Development', package: 'di', load: () => import('react-icons/di') },
    { name: 'Octicons', prefix: 'Go', color: '#24292E', category: 'Development', package: 'go', load: () => import('react-icons/go') },
    { name: 'VS Code', prefix: 'Vsc', color: '#007ACC', category: 'Development', package: 'vsc', load: () => import('react-icons/vsc') },
    { name: 'Game Icons', prefix: 'Gi', color: '#FF6B6B', category: 'Games', package: 'gi', load: () => import('react-icons/gi') },
    { name: 'Weather Icons', prefix: 'Wi', color: '#4A90E2', category: 'Weather', package: 'wi', load: () => import('react-icons/wi') },
    { name: 'Themify', prefix: 'Ti', color: '#FF4081', category: 'UI', package: 'ti', load: () => import('react-icons/ti') },
    { name: 'Circum', prefix: 'Ci', color: '#6B7280', category: 'UI', package: 'ci', load: () => import('react-icons/ci') },
    { name: 'Linearicons', prefix: 'Lia', color: '#3B82F6', category: 'UI', package: 'lia', load: () => import('react-icons/lia') },
    { name: 'Bytesize', prefix: 'Tfi', color: '#4A5568', category: 'UI', package: 'tfi', load: () => import('react-icons/tfi') },
    { name: 'Core UI', prefix: 'Cg', color: '#6B7280', category: 'UI', package: 'cg', load: () => import('react-icons/cg') },
    { name: 'BoxIcons', prefix: 'Bi', color: '#3B82F6', category: 'UI', package: 'bi', load: () => import('react-icons/bi') },
    { name: 'IcoMoon', prefix: 'Im', color: '#8257E5', category: 'UI', package: 'im', load: () => import('react-icons/im') },
    { name: 'Eva Icons', prefix: 'Sl', color: '#7C3AED', category: 'UI', package: 'sl', load: () => import('react-icons/sl') },
];

const setByName = new Map(ICON_SETS.map((s) => [s.name, s]));
const cache = new Map<string, IconModule>();

/** Load (and memoize) a single icon set's module by its meta. */
export async function loadSet(meta: IconSetMeta): Promise<IconModule> {
    const cached = cache.get(meta.package);
    if (cached) return cached;
    const mod = (await meta.load()) as IconModule;
    cache.set(meta.package, mod);
    return mod;
}

/**
 * Resolve a single icon component from a stored value such as
 * "Font Awesome/FaHeart" or a bare "FaHeart". Loads only the set(s) needed.
 */
export async function resolveIcon(value: string): Promise<IconType | null> {
    // Preferred format: "Set Name/IconName" — load exactly one set.
    if (value.includes('/')) {
        const [setName, name] = value.split('/');
        const meta = setByName.get(setName);
        if (!meta) return null;
        const mod = await loadSet(meta);
        return mod[name] ?? null;
    }

    // Bare name: try only the sets whose prefix matches the name.
    const candidates = ICON_SETS.filter((s) => value.startsWith(s.prefix));
    for (const meta of candidates) {
        const mod = await loadSet(meta);
        if (mod[value]) return mod[value];
    }
    return null;
}
